import React, { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCampaign } from '../contexts/CampaignContext';
import { useMap } from '../contexts/MapContext';
import MapCanvas from '../components/canvas/MapCanvas';
import * as tokenAnimator from '../lib/tokenAnimator';
import BuffPanel from '../components/dm/BuffPanel';
import CharacterSetup from '../components/player/CharacterSetup';
import AppearanceEditor from '../components/player/AppearanceEditor';
import PartyHpStrip from '../components/player/PartyHpStrip';
import SpellSlotsCard from '../components/player/SpellSlotsCard';
import InventoryPanel from '../components/player/InventoryPanel';
import TradePanel from '../components/player/TradePanel';
import PendingTradeBanner from '../components/player/PendingTradeBanner';
import ChallengePanel from '../components/player/ChallengePanel';
import MinigameOverlay from '../components/player/MinigameOverlay';
import PendingChallengeBanner from '../components/player/PendingChallengeBanner';
import AbilityScoreCell from '../components/player/AbilityScoreCell';
import WeaponProficiencyPicker from '../components/player/WeaponProficiencyPicker';
import { downloadCharacter, parseImportedJson, summarize } from '../lib/characterIO';
import DiceTray from '../components/dice/DiceTray';
import DiceRollOverlay from '../components/dice/DiceRollOverlay';
import TrapReactionPrompt from '../components/player/TrapReactionPrompt';
import PerceptionPromptBanner from '../components/player/PerceptionPromptBanner';
import PassivePerceptionWatcher from '../components/player/PassivePerceptionWatcher';
import NearbyShopButton from '../components/player/NearbyShopButton';
import { rollDice } from '../lib/dice';
import { buildWallSegs, buildDoorVisionSegs, computeVisibleCells } from '../lib/vision';
import PlayerPresence from '../components/dm/PlayerPresence';
import ReferencePanel from '../components/reference/ReferencePanel';
import { pointToSegmentDist } from '../lib/geometry';

export default function PlayerView() {
  const { auth, logout }  = useAuth();
  const { tokens, players, initiative, updateToken, nextTurn, submitRoll, submitDiceRoll, spawnToken, assignToken, updatePlayer, createPing, removeToken, trades, proposeTrade, acceptTrade, declineTrade, cancelTrade, challenges, proposeChallenge, acceptChallenge, declineChallenge, cancelChallenge, submitChallengeScore, dismissChallenge, deleteChallenge, equipItemInInventory, useConsumable } = useCampaign();
  const { cells, gridW, gridH, paintFogCells, walls, doors, updateDoor } = useMap();
  const navigate = useNavigate();
  const { id }   = useParams();
  const mapRef   = useRef(null);

  const [showInitiative, setShowInitiative] = useState(false);
  const [showBuffs,      setShowBuffs]      = useState(false);
  const [showReference,  setShowReference]  = useState(false);
  const [pingMode,       setPingMode]       = useState(false);
  const [showLevelUp,    setShowLevelUp]    = useState(false);
  const [showAppearance, setShowAppearance] = useState(false);
  const [showInventory,  setShowInventory]  = useState(false);
  // Trade UI state — null = closed; { mode: 'compose' } or { mode: 'review', id }
  const [tradeUI,        setTradeUI]        = useState(null);
  // Challenge UI state. Mirrors trade pattern.
  //   null  | { mode: 'compose' } | { mode: 'review', id } | { mode: 'play', id }
  const [challengeUI,    setChallengeUI]    = useState(null);
  // Track challenges we've already auto-opened so closing the overlay
  // sticks (the effect won't re-open the same one on re-render).
  const autoOpenedChallengesRef = useRef(new Set());

  // Hidden <input type="file"> for character import. The menu item triggers
  // a synthetic click; the change handler reads + validates + restores.
  const importFileRef = useRef(null);
  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // reset so picking the same file twice still fires
    if (!file || !auth?.playerId) return;
    const text = await file.text();
    const result = parseImportedJson(text);
    if (!result.ok) {
      alert(`Import failed: ${result.error}`);
      return;
    }
    if (!confirm(`Import ${summarize(result.data)}? This replaces your current character.`)) return;
    await updatePlayer(auth.playerId, result.data);
  };

  const [deathRollInput, setDeathRollInput] = useState('');
  const [doorMsg, setDoorMsg] = useState('');
  const [rollInput, setRollInput] = useState('');
  const [rollMode,  setRollMode]  = useState('normal');
  const [rollSubmitted, setRollSubmitted] = useState(false);
  const [leaveConfirm,  setLeaveConfirm]  = useState(false);
  const [showMenu,      setShowMenu]      = useState(false);
  const [showDice,      setShowDice]      = useState(false);

  const myToken = Object.values(tokens).find((t) => t.ownerId === auth?.playerId && !t.mirrorDuplicate);
  const playerRecord = players[auth?.playerId];

  // Auto-open the play overlay when a challenge involving me flips to
  // 'active' and I haven't yet submitted my score. Both players land on
  // the countdown together without anyone needing to tap a banner.
  useEffect(() => {
    if (!auth?.playerId) return;
    const target = Object.values(challenges || {}).find((c) => {
      if (c.status !== 'active') return false;
      if (autoOpenedChallengesRef.current.has(c.id)) return false;
      const isFrom = c.fromPlayerId === auth.playerId;
      const isTo   = c.toPlayerId   === auth.playerId;
      if (!isFrom && !isTo) return false;
      const myScore = isFrom ? c.fromScore : c.toScore;
      return myScore == null;
    });
    if (target) {
      autoOpenedChallengesRef.current.add(target.id);
      setChallengeUI({ mode: 'play', id: target.id });
    }
  }, [challenges, auth?.playerId]);

  // Heartbeat — update lastSeen every 10s so DM knows we're connected
  useEffect(() => {
    if (!auth?.playerId) return;
    const tick = () => updatePlayer(auth.playerId, { lastSeen: Date.now(), connected: true, attentive: !document.hidden });
    tick();
    const id_ = setInterval(tick, 10000);
    const onLeave = () => updatePlayer(auth.playerId, { connected: false, attentive: false, lastSeen: Date.now() });
    const onVisChange = () => updatePlayer(auth.playerId, { attentive: !document.hidden, lastSeen: Date.now() });
    window.addEventListener('beforeunload', onLeave);
    document.addEventListener('visibilitychange', onVisChange);
    return () => { clearInterval(id_); window.removeEventListener('beforeunload', onLeave); document.removeEventListener('visibilitychange', onVisChange); };
  }, [auth?.playerId, updatePlayer]);

  // DM deleted us mid-session — once we've loaded a record at least once and
  // then it disappears from the snapshot, force a clean logout so the device
  // stops heartbeating and the player goes back to login.
  const everHadRecordRef = useRef(false);
  useEffect(() => {
    if (!auth?.playerId) return;
    if (playerRecord) { everHadRecordRef.current = true; return; }
    if (everHadRecordRef.current && !playerRecord) {
      logout();
      navigate(`/session/${id}/player`, { replace: true });
    }
  }, [playerRecord, auth?.playerId, logout, navigate, id]);

  // Auto-create a hidden token for returning players who have saved stats but no token
  const autoSpawnDone = useRef(false);
  useEffect(() => {
    if (autoSpawnDone.current || myToken || !playerRecord?.charName) return;
    autoSpawnDone.current = true;
    (async () => {
      const tokenId = await spawnToken({
        name:            playerRecord.charName,
        type:            'player',
        colour:          playerRecord.colour || '#c084fc',
        hp:              playerRecord.maxHp || 10,
        maxHp:           playerRecord.maxHp || 10,
        ac:              playerRecord.ac || 10,
        initiativeBonus: playerRecord.initiativeBonus || 0,
        speed:           playerRecord.speed || 30,
        visionRange:     playerRecord.visionRange ?? 60,
        class:           playerRecord.class || null,
        level:           playerRecord.level || 1,
        sizeCategory:    playerRecord.sizeCategory || 'medium',
        // Carry character art forward — otherwise rejoin falls back to the
        // procedural class sprite ("old style").
        paperdoll:       playerRecord.paperdoll || null,
        combatStyle:     playerRecord.combatStyle || null,
        packKey:         playerRecord.packKey || null,
        ownerId:         auth.playerId,
        hidden:          true,
      });
      // assignToken still updates the player's tokenId pointer; ownerId is
      // already set above in the same write that creates the token.
      await assignToken(tokenId, auth.playerId);
    })();
  }, [myToken, playerRecord, auth?.playerId]);

  // Auto-reveal fog within line of sight — once revealed, stays revealed
  const wallSegs       = useMemo(() => buildWallSegs(walls), [walls]);
  const doorVisionSegs = useMemo(() => buildDoorVisionSegs(doors, false), [doors]);
  const allVisionSegs  = useMemo(() => [...wallSegs, ...doorVisionSegs], [wallSegs, doorVisionSegs]);
  useEffect(() => {
    if (!myToken || myToken.hidden) return;
    const size    = myToken.size || 1;
    const range   = (myToken.visionRange || 60) / 5;
    const visible = computeVisibleCells(myToken.x, myToken.y, size, allVisionSegs, cells, gridW, gridH, range);
    if (visible.length) paintFogCells(visible, true);
  }, [myToken?.x, myToken?.y, myToken?.hidden, myToken?.visionRange, allVisionSegs, cells, gridW, gridH, paintFogCells]);

  const { phase = 'inactive', turnOrder = [], activeIndex = 0, round = 0, pendingRolls = {}, battleScreen = false, combatEndScreen = false } = initiative;

  // Find nearest door segment within interaction range of the player token
  const adjacentDoor = useMemo(() => {
    if (!myToken || !doors || doors.length === 0) return null;
    const size = myToken.size || 1;
    const cx   = myToken.x + size / 2;
    const cy   = myToken.y + size / 2;
    let best = null, bestDist = 1.2; // must be in neighboring cell
    for (const door of doors) {
      if (door.type === 'secret' && !door.secretRevealed) continue;
      const [a, b] = door.pts;
      const dist = pointToSegmentDist(cx, cy, a.x, a.y, b.x, b.y);
      if (dist < bestDist) { best = door; bestDist = dist; }
    }
    return best;
  }, [myToken, doors]);

  const handleDoorInteract = useCallback(() => {
    if (!adjacentDoor) return;
    const isBars = adjacentDoor.type === 'bars';
    const state  = adjacentDoor.state || 'closed';
    if (state === 'locked') {
      setDoorMsg(isBars ? '🔒 Bars are locked.' : '🔒 Door is locked.');
      setTimeout(() => setDoorMsg(''), 2500);
      return;
    }
    if (state === 'open') {
      updateDoor(adjacentDoor.id, { state: 'closed' });
      setDoorMsg(isBars ? '⛓ Bars closed.' : '🚪 Door closed.');
    } else {
      updateDoor(adjacentDoor.id, { state: 'open' });
      setDoorMsg(isBars ? '⛓ Bars opened.' : '🚪 Door opened.');
    }
    setTimeout(() => setDoorMsg(''), 2000);
  }, [adjacentDoor, updateDoor]);

  const handleHP = useCallback(async (delta) => {
    if (!myToken) return;
    let newTemp = myToken.tempHp || 0;
    let adj     = delta;
    if (adj < 0 && newTemp > 0) {
      const absorbed = Math.min(newTemp, Math.abs(adj));
      newTemp -= absorbed;
      adj     += absorbed;
    }
    const newHp = Math.max(0, Math.min(myToken.maxHp, myToken.hp + adj));
    await updateToken(myToken.id, { hp: newHp, tempHp: newTemp });
  }, [myToken, updateToken]);

  const handleSetFacing = useCallback((dir) => {
    if (!myToken) return;
    tokenAnimator.setFacing(myToken.id, dir);
    updateToken(myToken.id, { facing: dir });
  }, [myToken, updateToken]);

  const handleDeathRoll = useCallback(() => {
    if (!myToken || !deathRollInput) return;
    const roll = parseInt(deathRollInput, 10);
    if (isNaN(roll) || roll < 1 || roll > 20) return;
    const ds = myToken.deathSaves || { successes: 0, failures: 0 };

    if (roll === 20) {
      // Nat 20: conscious at 1 HP, clear death saves
      updateToken(myToken.id, { hp: 1, deathSaves: null });
    } else if (roll === 1) {
      // Nat 1: 2 failures
      updateToken(myToken.id, { deathSaves: { successes: ds.successes, failures: Math.min(3, ds.failures + 2) } });
    } else if (roll >= 10) {
      // Success
      updateToken(myToken.id, { deathSaves: { successes: Math.min(3, ds.successes + 1), failures: ds.failures } });
    } else {
      // Failure
      updateToken(myToken.id, { deathSaves: { successes: ds.successes, failures: Math.min(3, ds.failures + 1) } });
    }
    setDeathRollInput('');
  }, [myToken, deathRollInput, updateToken]);

  const handleLogout = () => { logout(); navigate(`/session/${id}/player/login`); };

  // Two-tap leave-board: first tap arms a 4s confirm window; second tap commits.
  // Removes only the token (character record stays); player drops into waiting
  // state, DM can re-place via SpawnPanel "Create".
  useEffect(() => {
    if (!leaveConfirm) return;
    const t = setTimeout(() => setLeaveConfirm(false), 4000);
    return () => clearTimeout(t);
  }, [leaveConfirm]);
  const handleLeaveBoard = async () => {
    if (!leaveConfirm) { setLeaveConfirm(true); return; }
    setLeaveConfirm(false);
    if (myToken) await removeToken(myToken.id);
    // DM-created players have no saved character — there's nothing to return to,
    // so drop them at login. Players with a saved character land on the waiting
    // screen and can be re-placed by the DM.
    if (!playerRecord?.charName) {
      logout();
      navigate(`/session/${id}/player/login`);
    }
  };

  const hpPct    = myToken ? Math.max(0, myToken.hp / myToken.maxHp) : 0;
  const hpColour = hpPct > 0.5 ? '#4ade80' : hpPct > 0.35 ? '#fb923c' : hpPct > 0.15 ? '#facc15' : '#f87171';

  const handleSubmitRoll = async () => {
    if (!myToken || !rollInput) return;
    const v = parseInt(rollInput, 10);
    if (Number.isFinite(v)) {
      const bonus = myToken.initiativeBonus || 0;
      // Manual physical-die roll — still broadcast so the Display animates and
      // the roll lands in the log alongside digital rolls.
      const crit = v === 20 ? 'crit' : v === 1 ? 'fumble' : null;
      await submitDiceRoll({
        playerId:   auth?.playerId ?? null,
        playerName: myToken.name || playerRecord?.charName || auth?.name || 'Player',
        colour:     myToken.colour || playerRecord?.colour || '#c084fc',
        isDm:       false,
        dice:       [{ sides: 20, results: [v], kept: [v] }],
        modifier:   bonus,
        mode:       'normal',
        total:      v + bonus,
        crit,
        label:      'Initiative',
      });
    }
    await submitRoll(myToken.id, rollInput);
    setRollSubmitted(true);
  };

  // Digital path: roll the d20 in-app, broadcast it, submit it as initiative.
  const handleInitiativeRoll = async () => {
    if (!myToken) return;
    const bonus = myToken.initiativeBonus || 0;
    const result = rollDice({
      dice:     [{ sides: 20, count: 1 }],
      modifier: bonus,
      mode:     rollMode,
    });
    await submitDiceRoll({
      ...result,
      playerId:   auth?.playerId ?? null,
      playerName: myToken.name || playerRecord?.charName || auth?.name || 'Player',
      colour:     myToken.colour || playerRecord?.colour || '#c084fc',
      isDm:       false,
      label:      'Initiative',
    });
    const keptD20 = result.dice[0].kept[0];
    await submitRoll(myToken.id, keptD20);
    setRollSubmitted(true);
  };

  const isRolling    = phase === 'rolling';
  const inCombat     = phase === 'active' && turnOrder.length > 0;
  const myRoll       = myToken ? pendingRolls[myToken.id] : null;
  const hasRolled    = myRoll !== null && myRoll !== undefined;
  const activeTokenId = turnOrder[activeIndex] ?? null;
  const isMyTurn     = myToken && myToken.id === activeTokenId;

  // Play a notification ping when it becomes this player's turn
  const prevIsMyTurn = useRef(false);
  useEffect(() => {
    if (isMyTurn && !prevIsMyTurn.current) {
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = 'sine';
        osc.frequency.setValueAtTime(880, ctx.currentTime);
        osc.frequency.setValueAtTime(1320, ctx.currentTime + 0.1);
        gain.gain.setValueAtTime(0.3, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.3);
      } catch (e) { /* audio context may not be available */ }
    }
    prevIsMyTurn.current = isMyTurn;
  }, [isMyTurn]);

  // ── No token yet — show character setup (unless returning player auto-spawning) ──
  if (!myToken && !playerRecord?.charName && !isRolling && !battleScreen) {
    return <CharacterSetup />;
  }

  // ── Waiting-for-DM lobby ───────────────────────────────────────────────
  // The token is created hidden until the DM places it. Players don't need
  // to wait helplessly — they can still set up / refine their character,
  // edit inventory, level up, trade, and challenge other players. The
  // floating "waiting" chip below makes the placement state legible
  // without blocking any of the UI.
  const isWaitingForPlacement = !!myToken?.hidden;

  const handleReroll = async () => {
    if (myToken) await removeToken(myToken.id);
    // Clear the saved character so CharacterSetup re-renders on next snapshot.
    // Stats stay where they are if the player wants to tweak; charName is the
    // gate that decides whether auto-spawn fires.
    autoSpawnDone.current = false;
    await updatePlayer(auth.playerId, { charName: null });
  };

  // ── Battle start screen ────────────────────────────────────────────────
  if (battleScreen) {
    return (
      <div style={styles.battleScreen}>
        <div style={styles.battleContent}>
          <div style={styles.battleSwords}>⚔</div>
          <div style={styles.battleTitle}>BATTLE BEGINS</div>
          <div style={styles.battleSub}>Round 1</div>
        </div>
      </div>
    );
  }

  // ── Combat end screen ──────────────────────────────────────────────────
  if (combatEndScreen) {
    const isDefeat = (initiative.combatOutcome ?? 'victory') === 'defeat';
    return (
      <div style={styles.battleScreen}>
        <div style={styles.battleContent}>
          <div style={{ fontSize: 80, marginBottom: 24 }}>{isDefeat ? '💀' : '🛡'}</div>
          <div style={{ ...styles.battleTitle, color: isDefeat ? '#c0392b' : '#4ade80', textShadow: `0 0 40px ${isDefeat ? 'rgba(192,57,43,0.8)' : 'rgba(74,222,128,0.8)'}, 0 0 80px ${isDefeat ? 'rgba(192,57,43,0.4)' : 'rgba(74,222,128,0.4)'}` }}>
            {isDefeat ? 'DEFEAT' : 'VICTORY'}
          </div>
          <div style={styles.battleSub}>{isDefeat ? 'The party has fallen...' : 'The battle is won!'}</div>
        </div>
      </div>
    );
  }

  // ── Rolling phase screen ───────────────────────────────────────────────
  if (isRolling) {
    return (
      <div style={styles.layout}>
        <div style={styles.topBar}>
          <div style={styles.playerName}>{auth?.name || 'Player'}</div>
          <button style={styles.iconBtn} onClick={handleLogout}>↩</button>
        </div>
        <div style={styles.rollPhaseScreen}>
          <div style={styles.rollPhaseContent}>
            <div style={styles.rollPhaseIcon}>⚔️</div>
            <h2 style={styles.rollPhaseTitle}>Combat is starting!</h2>
            <p style={styles.rollPhaseSubtitle}>Tap Roll for the app to roll your d20 — or roll a physical die and enter the result.</p>
            {myToken ? (
              hasRolled ? (
                <div style={styles.rollSubmittedCard}>
                  <div style={styles.rollSubmittedIcon}>✓</div>
                  <div style={styles.rollSubmittedText}>Roll submitted!</div>
                  <div style={styles.rollSubmittedTotal}>
                    {myRoll.roll}{myToken.initiativeBonus ? ` + ${myToken.initiativeBonus}` : ''} = <strong>{myRoll.total}</strong>
                  </div>
                  <div style={styles.rollWaiting}>Waiting for DM to begin battle…</div>
                </div>
              ) : (
                <div style={styles.rollInputCard}>
                  {/* Locked formula chip — same number that lands as initiative */}
                  <div style={styles.rollFormulaChip}>
                    1d20{myToken.initiativeBonus
                      ? (myToken.initiativeBonus > 0
                          ? ` + ${myToken.initiativeBonus}`
                          : ` − ${Math.abs(myToken.initiativeBonus)}`)
                      : ''}
                  </div>

                  {/* Digital path: tap to roll, broadcast, submit. */}
                  <div style={styles.rollAdvRow}>
                    {[
                      { id: 'disadvantage', label: 'Dis',    color: '#c03333' },
                      { id: 'normal',       label: 'Normal', color: '#6a7a8a' },
                      { id: 'advantage',    label: 'Adv',    color: '#5cb870' },
                    ].map((opt) => {
                      const active = rollMode === opt.id;
                      return (
                        <button
                          key={opt.id}
                          onClick={() => setRollMode(opt.id)}
                          style={{
                            ...styles.rollAdvBtn,
                            ...(active ? { borderColor: opt.color, color: opt.color, background: `${opt.color}24` } : {}),
                          }}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                  <button style={styles.rollDigitalBtn} onClick={handleInitiativeRoll}>
                    🎲 Roll!
                  </button>

                  <div style={styles.rollDivider}>— or roll a physical die —</div>

                  {/* Manual path preserved for players using physical dice. */}
                  <div style={styles.rollInputLabel}>Enter your d20</div>
                  <input
                    style={styles.rollBigInput}
                    type="number" min="1" max="20" placeholder="—"
                    value={rollInput}
                    onChange={(e) => setRollInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSubmitRoll()}
                  />
                  <button style={styles.rollSubmitBtn} onClick={handleSubmitRoll} disabled={!rollInput}>
                    Submit Roll
                  </button>
                </div>
              )
            ) : (
              <div style={styles.rollWaiting}>Waiting for DM to assign your token…</div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.layout}>
      {/* Hidden file input wired to the "📂 Import character" menu item */}
      <input
        ref={importFileRef}
        type="file"
        accept="application/json,.json"
        style={{ display: 'none' }}
        onChange={handleImportFile}
      />
      {/* Top bar — name, round, initiative, overflow menu */}
      <div style={styles.topBar}>
        <div style={styles.playerName}>{auth?.name || 'Player'}</div>
        {inCombat && <div style={styles.roundBadge}>Round {round}</div>}
        <PlayerPresence compact />
        <button style={styles.iconBtn} onClick={() => setShowInitiative((v) => !v)} title="Initiative order">⚔</button>
        <button
          style={styles.iconBtn}
          onClick={() => { setShowMenu((v) => !v); setLeaveConfirm(false); }}
          title="Menu"
        >⋯</button>
      </div>

      {/* Overflow menu — Leave Board / Logout */}
      {showMenu && (
        <>
          <div
            style={styles.menuBackdrop}
            onClick={() => { setShowMenu(false); setLeaveConfirm(false); }}
          />
          <div style={styles.menuPanel}>
            {myToken && (
              <button
                style={styles.menuItem}
                onClick={() => { setShowMenu(false); setShowAppearance(true); }}
              >
                🎨 Appearance
              </button>
            )}
            {myToken && (
              <button
                style={styles.menuItem}
                onClick={() => { setShowMenu(false); setShowLevelUp(true); }}
              >
                ⬆ Level {myToken.level || 1}
              </button>
            )}
            <button
              style={styles.menuItem}
              onClick={() => { setShowMenu(false); setShowReference(true); }}
            >
              📖 Reference
            </button>
            <button
              style={styles.menuItem}
              onClick={() => { setShowMenu(false); setChallengeUI({ mode: 'compose' }); }}
            >
              🎯 Challenge
            </button>
            {playerRecord && (
              <button
                style={styles.menuItem}
                onClick={() => { setShowMenu(false); downloadCharacter(playerRecord); }}
              >
                💾 Export character
              </button>
            )}
            <button
              style={styles.menuItem}
              onClick={() => { setShowMenu(false); importFileRef.current?.click(); }}
            >
              📂 Import character
            </button>
            <button
              style={leaveConfirm ? { ...styles.menuItem, ...styles.menuItemDanger } : styles.menuItem}
              onClick={() => {
                if (!leaveConfirm) { setLeaveConfirm(true); return; }
                setShowMenu(false);
                handleLeaveBoard();
              }}
            >
              {leaveConfirm ? '🚪 Tap again to confirm' : '🚪 Leave the board'}
            </button>
            <button
              style={styles.menuItem}
              onClick={() => { setShowMenu(false); handleLogout(); }}
            >
              ↩ Sign out
            </button>
          </div>
        </>
      )}

      <PartyHpStrip myTokenId={myToken?.id} activeTokenId={activeTokenId} />

      {/* Reference overlay */}
      {showReference && (
        <div style={styles.buffsOverlay} onClick={(e) => { if (e.target === e.currentTarget) setShowReference(false); }}>
          <div style={{ ...styles.buffsPanel, maxWidth: 440, maxHeight: '90vh' }}>
            <div style={styles.buffsPanelHeader}>
              <span>📖 Reference</span>
              <button style={styles.closeBtn} onClick={() => setShowReference(false)}>✕</button>
            </div>
            <div style={{ ...styles.buffsPanelBody, padding: 0, display: 'flex', flexDirection: 'column' }}>
              <ReferencePanel />
            </div>
          </div>
        </div>
      )}

      {/* Buffs overlay */}
      {showBuffs && myToken && (
        <div style={styles.buffsOverlay}>
          <div style={styles.buffsPanel}>
            <div style={styles.buffsPanelHeader}>
              <span>✨ Buffs & Effects</span>
              <button style={styles.closeBtn} onClick={() => setShowBuffs(false)}>✕</button>
            </div>
            <div style={styles.buffsPanelBody}>
              <BuffPanel token={myToken} />
            </div>
          </div>
        </div>
      )}

      {/* Initiative overlay */}
      {showInitiative && (
        <div style={styles.initiativeOverlay}>
          <div style={styles.initiativePanel}>
            <div style={styles.initiativeHeader}>
              <span>Initiative Order</span>
              <button style={styles.closeBtn} onClick={() => setShowInitiative(false)}>✕</button>
            </div>
            <ol style={styles.initiativeList}>
              {turnOrder.map((tokenId, idx) => {
                const t = tokens[tokenId];
                const active = idx === activeIndex;
                if (!t) return null;
                return (
                  <li key={tokenId} style={{ ...styles.initEntry, ...(active ? styles.initActive : {}) }}>
                    <span style={{ ...styles.initDot, background: t.colour || '#888' }} />
                    <span style={{ flex: 1 }}>{t.name}</span>
                    {active && <span style={{ color: '#c084fc' }}>▶</span>}
                  </li>
                );
              })}
              {turnOrder.length === 0 && (
                <li style={{ padding: '12px 0', color: '#888aaa', textAlign: 'center' }}>No active combat</li>
              )}
            </ol>
          </div>
        </div>
      )}

      {/* Map */}
      <div style={styles.mapArea}>
        <MapCanvas
          ref={mapRef}
          isDm={false}
          myTokenId={myToken?.id}
          followTokenId={myToken?.id}
          onCellClick={pingMode ? (x, y) => {
            createPing(auth.playerId, x, y, myToken?.colour || '#60a5fa');
            setPingMode(false);
          } : null}
        />
        {pingMode && (
          <div style={styles.pingHint}>📍 Tap the map to ping · <button style={styles.pingHintCancel} onClick={() => setPingMode(false)}>Cancel</button></div>
        )}
        {isWaitingForPlacement && (
          <div style={styles.waitingChip}>
            ⏳ Waiting for DM to place you · You can edit your character, inventory, and stats anytime ·
            <button style={styles.waitingRerollBtn} onClick={handleReroll}>↻ Re-roll</button>
          </div>
        )}
        {inCombat && isMyTurn && <div style={styles.turnBanner}>⚡ Your Turn!</div>}
        {inCombat && !isMyTurn && activeTokenId && tokens[activeTokenId] && (
          <div style={styles.otherTurnBanner}>
            <span style={{ ...styles.turnDot, background: tokens[activeTokenId].colour || '#888' }} />
            {tokens[activeTokenId].name}'s Turn
          </div>
        )}

        {/* Facing direction pad — bottom left of map */}
        {myToken && (
          <div style={styles.facingPad}>
            <div style={styles.facingPadLabel}>Facing</div>
            <div style={styles.facingPadGrid}>
              <div />
              <button style={styles.facingBtn(myToken.facing === 'up')}    onClick={() => handleSetFacing('up')}>▲</button>
              <div />
              <button style={styles.facingBtn(myToken.facing === 'left')}  onClick={() => handleSetFacing('left')}>◀</button>
              <div />
              <button style={styles.facingBtn(myToken.facing === 'right')} onClick={() => handleSetFacing('right')}>▶</button>
              <div />
              <button style={styles.facingBtn(myToken.facing === 'down')}  onClick={() => handleSetFacing('down')}>▼</button>
              <div />
            </div>
          </div>
        )}
      </div>

      {/* Pending trade banner — sits above the bottom bar; tap to review */}
      {auth?.playerId && (
        <PendingTradeBanner
          trades={trades}
          myPlayerId={auth.playerId}
          onOpen={(id) => setTradeUI({ mode: 'review', id })}
        />
      )}

      {/* Pending challenge banner — pending review, active game, or recent result */}
      {auth?.playerId && (
        <PendingChallengeBanner
          challenges={challenges}
          myPlayerId={auth.playerId}
          onOpenReview={(id) => setChallengeUI({ mode: 'review', id })}
          onOpenActive={(id) => setChallengeUI({ mode: 'play',   id })}
          onOpenResult={(id) => setChallengeUI({ mode: 'play',   id })}
        />
      )}

      {/* Bottom bar — HP bars on top, 4-column action grid below.
          Conditional cards (death, door, status pills) appear above. */}
      <div style={styles.bottomBar}>

        {/* Death save UI — urgent, stays inline above main row */}
        {myToken && myToken.hp <= 0 && myToken.deathSaves && (
          <div style={styles.deathSavePanel}>
            <div style={styles.deathSaveTitle}>💀 Death Saves</div>
            <div style={styles.deathSaveRow}>
              <div style={styles.deathSaveGroup}>
                <span style={styles.deathSaveLabel}>Successes</span>
                <div style={styles.deathSavePips}>
                  {[0, 1, 2].map((i) => (
                    <span key={i} style={{ ...styles.deathSavePip, background: i < myToken.deathSaves.successes ? '#4ade80' : '#1a2a48' }} />
                  ))}
                </div>
              </div>
              <div style={styles.deathSaveGroup}>
                <span style={styles.deathSaveLabel}>Failures</span>
                <div style={styles.deathSavePips}>
                  {[0, 1, 2].map((i) => (
                    <span key={i} style={{ ...styles.deathSavePip, background: i < myToken.deathSaves.failures ? '#f87171' : '#1a2a48' }} />
                  ))}
                </div>
              </div>
            </div>
            {myToken.deathSaves.successes >= 3 ? (
              <div style={styles.deathSaveResult}>🛡 Stabilized!</div>
            ) : myToken.deathSaves.failures >= 3 ? (
              <div style={{ ...styles.deathSaveResult, color: '#f87171' }}>💀 Dead</div>
            ) : (
              <div style={styles.deathSaveInput}>
                <span style={styles.deathRollLabel}>d20 Roll</span>
                <input
                  style={styles.deathRollInput}
                  type="number" min="1" max="20" placeholder="—"
                  value={deathRollInput}
                  onChange={(e) => setDeathRollInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleDeathRoll();
                  }}
                />
                <button style={styles.deathRollBtn} onClick={handleDeathRoll} disabled={!deathRollInput}>
                  Roll
                </button>
              </div>
            )}
          </div>
        )}

        {/* Door prompt — also urgent */}
        {adjacentDoor && (
          <div style={styles.doorPrompt}>
            <button style={styles.doorBtn} onClick={handleDoorInteract}>
              {adjacentDoor.type === 'bars'
                ? (adjacentDoor.state === 'open' ? '⛓ Close Bars' : '⛓ Open Bars')
                : (adjacentDoor.state === 'open' ? '🚪 Close Door' : '🚪 Open Door')}
            </button>
            {doorMsg && <span style={styles.doorMsg}>{doorMsg}</span>}
          </div>
        )}

        {/* Status pills — wrap inline above HP bars */}
        {myToken && ((myToken.conditions?.length > 0) || (myToken.buffs?.length > 0)) && (
          <div style={styles.statusPills}>
            {myToken.conditions?.map((c) => (
              <span key={c} style={styles.condPill}>{c}</span>
            ))}
            {myToken.buffs?.map((b, i) => {
              const expiringSoon = !b.permanent && b.duration <= 1;
              return (
                <span key={i} style={{ ...styles.buffPill, borderColor: b.color || '#c084fc', color: b.color || '#c084fc' }}>
                  {b.icon} {b.label}
                  {!b.permanent && (
                    <span
                      style={{ ...styles.buffDurBadge, ...(expiringSoon ? styles.buffDurBadgeWarn : {}) }}
                      title={`${b.duration} round${b.duration === 1 ? '' : 's'} left`}
                    >
                      ⏳ {b.duration}
                    </span>
                  )}
                  <button
                    style={styles.buffPillX}
                    onClick={() => updateToken(myToken.id, { buffs: myToken.buffs.filter((_, j) => j !== i) })}
                    title="Remove buff"
                  >✕</button>
                </span>
              );
            })}
          </div>
        )}

        {myToken ? (
          <>
            {/* HP bar — full width with inline +/- and centered label */}
            <HpBar
              label={`Health ${myToken.hp}/${myToken.maxHp}`}
              pct={hpPct}
              fillColor={hpColour}
              onMinus={() => handleHP(-1)}
              onPlus={() => handleHP(1)}
              minusDisabled={myToken.hp <= 0}
              plusDisabled={myToken.hp >= myToken.maxHp}
            />

            {/* Temp HP bar — always under the HP bar, matching the mockup */}
            <HpBar
              label={`Temp HP ${myToken.tempHp || 0}`}
              pct={Math.min(1, (myToken.tempHp || 0) / Math.max(1, myToken.maxHp))}
              fillColor="#60a5fa"
              tinted
              onMinus={() => updateToken(myToken.id, { tempHp: Math.max(0, (myToken.tempHp || 0) - 1) })}
              onPlus={() => updateToken(myToken.id, { tempHp: (myToken.tempHp || 0) + 1 })}
              minusDisabled={(myToken.tempHp || 0) <= 0}
            />

            {/* Movement bar — full width during your combat turn */}
            {inCombat && isMyTurn && (() => {
              const speedCells = Math.floor((myToken.speed || 30) / 5) * (myToken.dashed ? 2 : 1);
              const used = myToken.movementUsed || 0;
              const remaining = Math.max(0, speedCells - used);
              return (
                <div style={styles.movementBarWrap}>
                  <div style={styles.movementTrack}>
                    <div style={{ ...styles.movementFill, width: `${(remaining / speedCells) * 100}%` }} />
                    <span style={styles.movementLabel}>
                      Movement {used * 5}/{speedCells * 5} ft{myToken.dashed ? ' (Dash)' : ''}
                    </span>
                  </div>
                </div>
              );
            })()}

            {/* Action grid — Bag | Dice | ActionStack | Ping/Challenge */}
            <div style={styles.actionGrid}>
              <button
                style={{ ...styles.gridPanel, ...styles.bagPanel, ...(showInventory ? styles.gridPanelActive : {}) }}
                onClick={() => setShowInventory(true)}
                disabled={!playerRecord}
                title="Inventory & wallet"
              >
                <span style={styles.panelIcon}>🎒</span>
                <span style={styles.panelLabel}>Bag</span>
              </button>

              <button
                style={{ ...styles.gridPanel, ...styles.dicePanel, ...(showDice ? styles.gridPanelActive : {}) }}
                onClick={() => setShowDice((v) => !v)}
                title="Dice tray"
              >
                <span style={styles.panelIcon}>🎲</span>
                <span style={styles.panelLabel}>Dice</span>
              </button>

              {(() => {
                const prone   = myToken.conditions?.includes('prone');
                const engaged = !!myToken.engaged;
                const dashed  = !!myToken.dashed;
                const speedCells = Math.floor((myToken.speed || 30) / 5);
                const usedMv  = myToken.movementUsed || 0;
                const standCost = Math.ceil(speedCells / 2);
                const toggleProne = () => {
                  const current = myToken.conditions || [];
                  const next = prone ? current.filter((c) => c !== 'prone') : [...current, 'prone'];
                  const patch = { conditions: next };
                  if (prone && inCombat && isMyTurn) {
                    patch.movementUsed = Math.min(speedCells, usedMv + standCost);
                  }
                  updateToken(myToken.id, patch);
                };
                return (
                  <div style={styles.actionStack}>
                    {inCombat && isMyTurn ? (
                      <button
                        style={{ ...styles.stackBtn, ...(dashed ? styles.stackBtnDash : {}) }}
                        onClick={() => updateToken(myToken.id, { dashed: !dashed })}
                        title="Dash — double your movement"
                      >🏃 {dashed ? 'Dashing' : 'Dash'}</button>
                    ) : (
                      <button style={{ ...styles.stackBtn, ...styles.stackBtnDisabled }} disabled>🏃 Dash</button>
                    )}
                    <button
                      style={{ ...styles.stackBtn, ...(showBuffs ? styles.stackBtnActive : {}) }}
                      onClick={() => setShowBuffs((v) => !v)}
                      title="Buffs panel"
                    >✨ Buff</button>
                    <button
                      style={{ ...styles.stackBtn, ...(prone ? styles.stackBtnProne : {}) }}
                      onClick={toggleProne}
                      title={prone ? `Stand up — costs ${standCost} squares` : 'Go prone — free action'}
                    >{prone ? `⬆️ Stand (−${standCost})` : '⬇️ Prone'}</button>
                    <button
                      style={{ ...styles.stackBtn, ...(engaged ? styles.stackBtnEngaged : {}) }}
                      onClick={() => updateToken(myToken.id, { engaged: !engaged })}
                      title={engaged ? 'Disengage' : 'Engage — mark in melee range'}
                    >⚔ {engaged ? 'Engaged' : 'Engage'}</button>
                    {playerRecord?.inspiration && (
                      <button
                        style={{ ...styles.stackBtn, ...styles.stackBtnInspired }}
                        onClick={() => {
                          // Mark as pending; DiceTray forces advantage on the next d20.
                          updatePlayer(auth.playerId, { inspiration: false, inspirationPending: true });
                          setShowDice(true);
                        }}
                        title="Spend Inspiration — advantage on your next d20 roll"
                      >✨ Use Inspiration</button>
                    )}
                  </div>
                );
              })()}

              <div style={styles.pingPanel}>
                <button
                  style={{ ...styles.halfPanel, ...(pingMode ? { background: myToken.colour || '#60a5fa', color: '#0a0f1e', borderColor: myToken.colour || '#60a5fa' } : {}) }}
                  onClick={() => setPingMode((v) => !v)}
                  title="Ping a spot on the map for the DM"
                >
                  <span style={styles.halfPanelIcon}>📍</span>
                  <span style={styles.halfPanelLabel}>Ping</span>
                </button>
                <button
                  style={{ ...styles.halfPanel, ...styles.halfPanelChallenge }}
                  onClick={() => setChallengeUI({ mode: 'compose' })}
                  title="Challenge another player to a minigame"
                  disabled={!playerRecord}
                >
                  <span style={styles.halfPanelIcon}>🎯</span>
                  <span style={styles.halfPanelLabel}>Challenge</span>
                </button>
                {/* Quick emote — trigger lives in the same column under
                    Ping/Challenge; the radial picker pops up centered on
                    screen so it's reachable from anywhere on the device. */}
                <EmoteRadial
                  emojis={['👍', '🍆', '💀', '😱', '❓', '💦', '🩹']}
                  onPick={(emoji) => {
                    if (!myToken) return;
                    updateToken(myToken.id, { lastEmote: { emoji, ts: Date.now() } });
                  }}
                />
              </div>
            </div>

            {playerRecord && (
              <SpellSlotsCard
                playerRecord={playerRecord}
                updatePlayer={updatePlayer}
              />
            )}

            {/* End Turn — full-width prominent button on your turn */}
            {inCombat && isMyTurn && (
              <button style={styles.endTurnFull} onClick={() => nextTurn()}>
                ⚔ End Turn
              </button>
            )}
          </>
        ) : (
          <div style={styles.noToken}>Waiting for DM to assign your token…</div>
        )}
      </div>

      {/* Level Up overlay */}
      {showLevelUp && myToken && (
        <LevelUpOverlay token={myToken} onClose={() => setShowLevelUp(false)} />
      )}

      {/* Appearance editor */}
      {showAppearance && (
        <AppearanceEditor onClose={() => setShowAppearance(false)} />
      )}

      {/* Inventory overlay — players self-manage their loot */}
      {showInventory && playerRecord && (
        <InventoryPanel
          inventory={playerRecord.inventory || []}
          currency={playerRecord.currency || {}}
          onInventoryChange={(next) => updatePlayer(auth.playerId, { inventory: next })}
          onCurrencyChange={(next)  => updatePlayer(auth.playerId, { currency: next })}
          editable
          onClose={() => setShowInventory(false)}
          onTrade={() => { setShowInventory(false); setTradeUI({ mode: 'compose' }); }}
          onEquip={(itemId, slot) => equipItemInInventory(auth.playerId, itemId, slot)}
          onUseConsumable={(itemId) => useConsumable(auth.playerId, myToken?.id, itemId)}
          playerRecord={playerRecord}
        />
      )}

      {/* Trade modal — compose (new proposal) or review (existing pending trade) */}
      {tradeUI && playerRecord && (
        <TradePanel
          mode={tradeUI.mode}
          myPlayerId={auth?.playerId}
          players={players}
          trade={tradeUI.mode === 'review' ? trades?.[tradeUI.id] : null}
          onClose={() => setTradeUI(null)}
          onSend={async (record) => {
            await proposeTrade(record);
            setTradeUI(null);
          }}
          onAccept={async () => {
            await acceptTrade(tradeUI.id);
            setTradeUI(null);
          }}
          onDecline={async () => {
            await declineTrade(tradeUI.id);
            setTradeUI(null);
          }}
          onCancel={async () => {
            await cancelTrade(tradeUI.id);
            setTradeUI(null);
          }}
        />
      )}

      {/* Challenge modal — compose (new) or review (pending) */}
      {challengeUI && (challengeUI.mode === 'compose' || challengeUI.mode === 'review') && (
        <ChallengePanel
          mode={challengeUI.mode}
          myPlayerId={auth?.playerId}
          players={players}
          challenge={challengeUI.mode === 'review' ? challenges?.[challengeUI.id] : null}
          onClose={() => setChallengeUI(null)}
          onSend={async (record) => {
            await proposeChallenge(record);
            setChallengeUI(null);
          }}
          onAccept={async () => {
            await acceptChallenge(challengeUI.id);
            // Switch from review → play immediately so we see the countdown.
            setChallengeUI({ mode: 'play', id: challengeUI.id });
          }}
          onDecline={async () => {
            await declineChallenge(challengeUI.id);
            setChallengeUI(null);
          }}
          onCancel={async () => {
            await cancelChallenge(challengeUI.id);
            setChallengeUI(null);
          }}
        />
      )}

      {/* Active game / result overlay */}
      {challengeUI?.mode === 'play' && challenges?.[challengeUI.id] && (
        <MinigameOverlay
          challenge={challenges[challengeUI.id]}
          myPlayerId={auth?.playerId}
          onSubmitScore={async (score) => {
            await submitChallengeScore(challengeUI.id, auth.playerId, score);
          }}
          onClose={async () => {
            // If the user closes the result screen, mark the challenge as
            // dismissed so the banner clears for both players.
            const ch = challenges?.[challengeUI.id];
            if (ch?.status === 'completed' && !ch.dismissedAt) {
              await dismissChallenge(challengeUI.id);
            }
            setChallengeUI(null);
          }}
        />
      )}

      {/* Dice tray — slide up from bottom edge */}
      {showDice && (
        <>
          <div style={styles.menuBackdrop} onClick={() => setShowDice(false)} />
          <div style={styles.diceTrayWrap}>
            <DiceTray
              identity={{
                playerId: auth?.playerId,
                playerName: myToken?.name || playerRecord?.charName || auth?.name || 'Player',
                colour: myToken?.colour || playerRecord?.colour || '#c084fc',
                isDm: false,
              }}
              onClose={() => setShowDice(false)}
              playerRecord={playerRecord}
            />
          </div>
        </>
      )}

      {/* Animated dice broadcast — every roll lands here.
          Position pinned to bottom-centre, just above the dice tray pull-up. */}
      <DiceRollOverlay viewerIdentity={{ playerId: auth?.playerId, isDm: false }} />

      {/* Trap reaction modal — fullscreen 3-second prompt when this player's
          token triggers a trap. Self-resolves on timeout. */}
      <TrapReactionPrompt />

      {/* Perception prompt — top banner when the DM asks for a roll. */}
      <PerceptionPromptBanner />

      {/* Always-on passive perception — auto-discovers in-range hidden things
          whose DC ≤ this player's passive score. Renders nothing. */}
      <PassivePerceptionWatcher />

      {/* Shop entry — appears when within 5 cells of a merchant NPC. */}
      <NearbyShopButton />
    </div>
  );
}

// ── Emote radial — trigger lives inline; the picker pops centered on screen
// when opened, so a thumb anywhere on the device can reach any emoji. Tap
// outside (backdrop) or pick an emoji to dismiss.
function EmoteRadial({ emojis, onPick }) {
  const [open, setOpen] = useState(false);
  const radius = 110;
  return (
    <>
      <button
        style={{ ...radialStyles.trigger, ...(open ? radialStyles.triggerActive : {}) }}
        onClick={() => setOpen((v) => !v)}
        title="Send an emote"
      >
        <span style={radialStyles.triggerIcon}>🙂</span>
        <span style={radialStyles.triggerLabel}>Emote</span>
      </button>
      {open && (
        <div style={radialStyles.overlay} onClick={() => setOpen(false)}>
          <div style={radialStyles.center} onClick={(e) => e.stopPropagation()}>
            <div style={radialStyles.hub}>🙂</div>
            {emojis.map((emoji, i) => {
              // Even spread around a full circle, first emoji at 12 o'clock.
              const angle = (i / emojis.length) * Math.PI * 2 - Math.PI / 2;
              const x = Math.cos(angle) * radius;
              const y = Math.sin(angle) * radius;
              return (
                <button
                  key={emoji}
                  style={{ ...radialStyles.petal, transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))` }}
                  onClick={() => { onPick(emoji); setOpen(false); }}
                >{emoji}</button>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}

const radialStyles = {
  // Trigger matches the halfPanel column shape so it sits cleanly under
  // Ping/Challenge.
  trigger:        { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, background: '#0e1528', border: '1px solid #4a3d28', borderRadius: 6, color: '#f0e6d0', fontFamily: "'Press Start 2P', monospace", cursor: 'pointer', padding: '6px 4px', minHeight: 0 },
  triggerActive:  { background: 'rgba(212,168,71,0.16)', borderColor: '#d4a847', boxShadow: '0 0 12px rgba(212,168,71,0.45)', color: '#d4a847' },
  triggerIcon:    { fontSize: 18, lineHeight: 1 },
  triggerLabel:   { fontSize: 10, fontWeight: 700, letterSpacing: '0.04em' },

  // Full-screen overlay with the picker centered.
  overlay:        { position: 'fixed', inset: 0, zIndex: 40, background: 'rgba(5,8,18,0.55)', backdropFilter: 'blur(2px)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  center:         { position: 'relative', width: 280, height: 280 },
  hub:            { position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', width: 56, height: 56, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, background: '#1a2a48', border: '2px solid #8b7340', boxShadow: '0 4px 18px rgba(0,0,0,0.6)' },
  petal:          { position: 'absolute', left: '50%', top: '50%', width: 54, height: 54, background: '#1a2a48', border: '2px solid #8b7340', borderRadius: '50%', color: '#f0e6d0', fontSize: 26, cursor: 'pointer', boxShadow: '0 4px 14px rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 },
};

// ── HP/Temp HP/etc. bar with inline +/- and centered label ─────────────────
function HpBar({ label, pct, fillColor, tinted, onMinus, onPlus, minusDisabled, plusDisabled }) {
  return (
    <div style={styles.hpBarRow}>
      <button
        style={styles.hpStepBtn}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={onMinus}
        disabled={minusDisabled}
      >−</button>
      <div style={{ ...styles.hpBarTrack, ...(tinted ? styles.hpBarTrackTemp : {}) }}>
        <div style={{ ...styles.hpBarTrackFill, width: `${Math.max(0, Math.min(100, pct * 100))}%`, background: fillColor }} />
        <span style={styles.hpBarTrackLabel}>{label}</span>
      </div>
      <button
        style={styles.hpStepBtn}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={onPlus}
        disabled={plusDisabled}
      >+</button>
    </div>
  );
}

// ── Level Up Overlay ──────────────────────────────────────────────────────────
const DEFAULT_ABILITY_SCORES = { str: 10, dex: 10, con: 10, int: 10, wis: 10, cha: 10 };

function LevelUpOverlay({ token, onClose }) {
  const { updateToken, players, updatePlayer } = useCampaign();
  const playerRecord  = token.ownerId ? players?.[token.ownerId] : null;
  const initAS        = { ...DEFAULT_ABILITY_SCORES, ...(playerRecord?.abilityScores || {}) };
  const currentLevel  = token.level || 1;
  const [newLevel,     setNewLevel]     = useState(currentLevel);
  const [newMaxHp,     setNewMaxHp]     = useState(token.maxHp || 10);
  const [newInitBonus, setNewInitBonus] = useState(token.initiativeBonus || 0);
  const [newSpeed,     setNewSpeed]     = useState(token.speed || 30);
  const [scores,       setScores]       = useState(initAS);
  const [profs,        setProfs]        = useState(playerRecord?.weaponProficiencies || []);
  const [saving,       setSaving]       = useState(false);
  const levelledUp = newLevel > currentLevel;
  const setScore = (key, val) => setScores((s) => ({ ...s, [key]: val }));

  const handleSave = async () => {
    setSaving(true);
    await updateToken(token.id, {
      level:           newLevel,
      maxHp:           newMaxHp,
      hp:              Math.min(token.hp, newMaxHp),
      initiativeBonus: newInitBonus,
      speed:           newSpeed,
    });
    if (playerRecord) {
      await updatePlayer(playerRecord.id, { abilityScores: scores, weaponProficiencies: profs });
    }
    setSaving(false);
    onClose();
  };

  return (
    <div style={lvlStyles.overlay}>
      <div style={lvlStyles.panel}>
        <div style={lvlStyles.header}>
          <span style={lvlStyles.title}>⬆ Character Stats</span>
          <button style={lvlStyles.closeBtn} onClick={onClose}>✕</button>
        </div>
        <div style={lvlStyles.body}>
          <div style={lvlStyles.charInfo}>
            <span style={{ ...lvlStyles.dot, background: token.colour }} />
            <span style={lvlStyles.charName}>{token.name}</span>
            <span style={lvlStyles.charClass}>{token.class}</span>
          </div>
          <StatStepper label="Level" value={newLevel} onChange={setNewLevel} min={1} max={20}
            extra={levelledUp && <div style={lvlStyles.levelUpBadge}>🎉 Level Up!</div>} />
          <StatStepper label="Max HP" value={newMaxHp} onChange={setNewMaxHp} min={1}
            hint="Roll your hit die + CON modifier and add to current max" />
          <StatStepper label="Initiative Bonus" value={newInitBonus} onChange={setNewInitBonus} min={-5} max={20} signed />
          <StatStepper label="Speed (ft)" value={newSpeed} onChange={setNewSpeed} min={0} max={120} step={5} />

          {playerRecord && (
            <>
              <div style={lvlStyles.sectionDivider}>Ability Scores</div>
              <div style={lvlStyles.abilityGrid}>
                <AbilityScoreCell label="STR" value={scores.str} onChange={(v) => setScore('str', v)} />
                <AbilityScoreCell label="DEX" value={scores.dex} onChange={(v) => setScore('dex', v)} />
                <AbilityScoreCell label="CON" value={scores.con} onChange={(v) => setScore('con', v)} />
                <AbilityScoreCell label="INT" value={scores.int} onChange={(v) => setScore('int', v)} />
                <AbilityScoreCell label="WIS" value={scores.wis} onChange={(v) => setScore('wis', v)} />
                <AbilityScoreCell label="CHA" value={scores.cha} onChange={(v) => setScore('cha', v)} />
              </div>
              <div style={lvlStyles.sectionDivider}>Weapon Proficiencies</div>
              <WeaponProficiencyPicker value={profs} onChange={setProfs} />
            </>
          )}
        </div>
        <div style={lvlStyles.footer}>
          <button className="btn-ghost" style={{ flex: 1 }} onClick={onClose}>Cancel</button>
          <button className="btn-primary" style={{ flex: 2 }} onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}


function StatStepper({ label, value, onChange, min = 0, max = 999, step = 1, signed, hint, extra }) {
  return (
    <div style={lvlStyles.field}>
      <label style={lvlStyles.label}>{label}</label>
      <div style={lvlStyles.stepper}>
        <button style={lvlStyles.stepBtn} onClick={() => onChange(Math.max(min, value - step))}>−</button>
        <span style={lvlStyles.stepVal}>{signed && value > 0 ? `+${value}` : value}</span>
        <button style={lvlStyles.stepBtn} onClick={() => onChange(Math.min(max, value + step))}>+</button>
      </div>
      {hint  && <div style={lvlStyles.hint}>{hint}</div>}
      {extra}
    </div>
  );
}

const lvlStyles = {
  overlay:      { position: 'absolute', inset: 0, background: 'rgba(5,8,18,0.8)', zIndex: 30, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: 12 },
  panel:        { background: 'linear-gradient(180deg, #1a2a48, #142038)', border: '2px solid #8b7340', borderRadius: 6, width: '100%', maxWidth: 380, boxShadow: '0 -8px 40px rgba(0,0,0,0.7)', overflow: 'hidden' },
  header:       { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', borderBottom: '2px solid #4a3d28' },
  title:        { fontWeight: 800, fontSize: 15, fontFamily: "'Press Start 2P', monospace", color: '#d4a847' },
  closeBtn:     { background: 'transparent', border: 'none', color: '#9a8b6e', fontSize: 18, cursor: 'pointer' },
  body:         { padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 14 },
  charInfo:     { display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#0e1528', borderRadius: 4, border: '1px solid #4a3d28' },
  dot:          { width: 12, height: 12, borderRadius: '50%', flexShrink: 0 },
  charName:     { fontWeight: 700, fontSize: 14, flex: 1, fontFamily: "'Press Start 2P', monospace" },
  charClass:    { fontSize: 12, color: '#9a8b6e' },
  field:        { display: 'flex', flexDirection: 'column', gap: 5 },
  label:        { fontSize: 11, fontWeight: 700, fontFamily: "'Press Start 2P', monospace", textTransform: 'uppercase', letterSpacing: '0.07em', color: '#9a8b6e' },
  stepper:      { display: 'flex', alignItems: 'center', background: '#0e1528', borderRadius: 4, overflow: 'hidden', border: '1px solid #4a3d28' },
  stepBtn:      { width: 48, height: 44, background: 'transparent', border: 'none', color: '#d4a847', fontSize: 22, fontWeight: 700, cursor: 'pointer', flexShrink: 0 },
  stepVal:      { flex: 1, textAlign: 'center', fontSize: 22, fontWeight: 800, color: '#f0e6d0' },
  levelUpBadge: { fontSize: 13, color: '#5cb870', fontWeight: 700, fontFamily: "'Press Start 2P', monospace", textAlign: 'center', background: 'rgba(74,154,90,0.12)', padding: '4px 10px', borderRadius: 4, marginTop: 4 },
  hint:         { fontSize: 11, color: '#6a5d45', lineHeight: 1.4 },
  footer:       { display: 'flex', gap: 8, padding: '12px 18px', borderTop: '2px solid #4a3d28' },
  sectionDivider: { fontSize: 11, fontWeight: 700, fontFamily: "'Press Start 2P', monospace", textTransform: 'uppercase', letterSpacing: '0.07em', color: '#d4a847', borderTop: '1px solid #4a3d28', paddingTop: 10, marginTop: 4 },
  abilityGrid:  { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 },
};

const styles = {
  layout:            { display: 'flex', flexDirection: 'column', width: '100%', height: '100%', overflow: 'hidden', background: '#0a0f1e' },
  topBar:            { height: 44, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 6, padding: '0 10px', background: 'linear-gradient(180deg, #1a2a48, #142038)', borderBottom: '2px solid #8b7340', zIndex: 10 },
  playerName:        { fontWeight: 700, fontSize: 15, flex: 1, minWidth: 0, color: '#d4a847', fontFamily: "'Press Start 2P', monospace", whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  menuBackdrop:      { position: 'fixed', inset: 0, zIndex: 49, background: 'transparent' },
  menuPanel:         { position: 'absolute', top: 50, right: 8, zIndex: 50, minWidth: 200, background: 'linear-gradient(180deg, #1a2a48, #142038)', border: '2px solid #8b7340', borderRadius: 6, boxShadow: '0 12px 32px rgba(0,0,0,0.7)', overflow: 'hidden', display: 'flex', flexDirection: 'column' },
  menuItem:          { padding: '12px 14px', textAlign: 'left', background: 'transparent', border: 'none', borderBottom: '1px solid rgba(74,61,40,0.4)', color: '#f0e6d0', fontSize: 14, fontWeight: 600, fontFamily: "'Press Start 2P', monospace", cursor: 'pointer' },
  menuItemDanger:    { background: 'rgba(192,57,43,0.18)', color: '#e07070' },
  menuItemActive:    { background: 'rgba(212,168,71,0.18)', color: '#d4a847' },
  diceTrayWrap:      { position: 'fixed', left: '50%', bottom: 0, transform: 'translateX(-50%)', zIndex: 60, width: '100%', display: 'flex', justifyContent: 'center', animation: 'toastSlideIn 220ms ease-out' },
  roundBadge:        { fontSize: 12, fontFamily: "'Press Start 2P', monospace", background: '#0e1528', padding: '2px 10px', borderRadius: 3, color: '#9a8b6e', border: '1px solid #4a3d28' },
  iconBtn:           { background: '#0e1528', border: '1px solid #4a3d28', color: '#f0e6d0', borderRadius: 4, width: 34, height: 34, fontSize: 16, cursor: 'pointer', padding: 0 },
  iconBtnDanger:     { background: '#3a1a1a', border: '1px solid #c03333', color: '#f87171', borderRadius: 4, width: 34, height: 34, fontSize: 16, fontWeight: 800, cursor: 'pointer', padding: 0, boxShadow: '0 0 8px rgba(192,51,51,0.45)' },
  mapArea:           { flex: 1, position: 'relative', overflow: 'hidden' },
  turnBanner:        { position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)', background: 'linear-gradient(180deg, #d4a847, #a68330)', color: '#1a0e00', fontWeight: 800, fontSize: 15, fontFamily: "'Press Start 2P', monospace", padding: '8px 24px', borderRadius: 4, boxShadow: '0 4px 20px rgba(212,168,71,0.5)', border: '1px solid #c4a44a', zIndex: 10, pointerEvents: 'none' },
  otherTurnBanner:   { position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)', background: 'rgba(10,15,30,0.92)', backdropFilter: 'blur(8px)', border: '2px solid #8b7340', color: '#f0e6d0', fontWeight: 700, fontSize: 13, fontFamily: "'Press Start 2P', monospace", padding: '7px 18px', borderRadius: 4, zIndex: 10, pointerEvents: 'none', display: 'flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' },
  turnDot:           { width: 10, height: 10, borderRadius: '50%', flexShrink: 0, display: 'inline-block' },
  facingPad:         { position: 'absolute', bottom: 16, left: 16, zIndex: 10, background: 'rgba(20,32,56,0.85)', backdropFilter: 'blur(8px)', border: '1px solid #4a3d28', borderRadius: 6, padding: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 },
  facingPadLabel:    { fontSize: 9, color: '#9a8b6e', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' },
  facingPadGrid:     { display: 'grid', gridTemplateColumns: 'repeat(3, 28px)', gridTemplateRows: 'repeat(3, 28px)', gap: 2 },
  facingBtn:         (active) => ({ background: active ? '#1a2a48' : '#0e1528', border: `1px solid ${active ? '#d4a847' : '#4a3d28'}`, color: active ? '#d4a847' : '#9a8b6e', borderRadius: 3, fontSize: 14, cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }),
  bottomBar:         { flexShrink: 0, background: 'linear-gradient(180deg, #1a2a48, #142038)', borderTop: '2px solid #8b7340', padding: '8px 10px 10px' },

  // HP / Temp HP / Movement bar shared row
  hpBarRow:          { display: 'flex', alignItems: 'stretch', gap: 6, marginBottom: 6 },
  hpStepBtn:         { width: 36, height: 26, background: '#0e1528', border: '1px solid #4a3d28', borderRadius: 4, color: '#f0e6d0', fontSize: 18, fontWeight: 800, cursor: 'pointer', padding: 0, lineHeight: 1, flexShrink: 0 },
  hpBarTrack:        { flex: 1, position: 'relative', height: 26, background: '#0e1528', border: '1px solid #4a3d28', borderRadius: 4, overflow: 'hidden' },
  hpBarTrackTemp:    { border: '1px solid #2a4a60' },
  hpBarTrackFill:    { position: 'absolute', top: 0, left: 0, height: '100%', borderRadius: 0, transition: 'width 0.3s ease', opacity: 0.55 },
  hpBarTrackLabel:   { position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, color: '#f0e6d0', textShadow: '0 1px 2px rgba(0,0,0,0.7)', fontFamily: "'Press Start 2P', monospace", letterSpacing: '0.04em' },

  // Movement bar (own row, no +/- buttons)
  movementBarWrap:   { marginBottom: 6 },
  movementTrack:     { position: 'relative', height: 22, background: '#0e1528', border: '1px solid #4a3d28', borderRadius: 4, overflow: 'hidden' },
  movementFill:      { position: 'absolute', top: 0, left: 0, height: '100%', background: '#4ade80', opacity: 0.45, transition: 'width 0.3s ease' },
  movementLabel:     { position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#f0e6d0', textShadow: '0 1px 2px rgba(0,0,0,0.7)', fontFamily: "'Press Start 2P', monospace", letterSpacing: '0.04em' },

  // Action grid: Bag | Dice | ActionStack | Ping/Challenge
  actionGrid:        { display: 'flex', gap: 6, marginTop: 4, alignItems: 'stretch' },
  gridPanel:         { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4, background: '#0e1528', border: '1px solid #4a3d28', borderRadius: 6, color: '#f0e6d0', fontFamily: "'Press Start 2P', monospace", cursor: 'pointer', padding: '10px 4px' },
  gridPanelActive:   { borderColor: '#d4a847', color: '#d4a847', boxShadow: '0 0 10px rgba(212,168,71,0.35)' },
  bagPanel:          { flex: '1 1 0', minWidth: 0, background: 'rgba(212,168,71,0.10)', borderColor: 'rgba(212,168,71,0.4)' },
  dicePanel:         { flex: '1.2 1 0', minWidth: 0 },
  pingPanel:         { flex: '0.9 1 0', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 },
  halfPanel:         { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, background: '#0e1528', border: '1px solid #4a3d28', borderRadius: 6, color: '#f0e6d0', fontFamily: "'Press Start 2P', monospace", cursor: 'pointer', padding: '6px 4px', minHeight: 0 },
  halfPanelActive:   { borderColor: '#d4a847', color: '#d4a847', boxShadow: '0 0 8px rgba(212,168,71,0.35)' },
  halfPanelChallenge:{ background: 'rgba(220,80,80,0.12)', borderColor: 'rgba(220,80,80,0.4)', color: '#f4a8a8' },
  halfPanelIcon:     { fontSize: 18, lineHeight: 1 },
  halfPanelLabel:    { fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' },
  panelIcon:         { fontSize: 22, lineHeight: 1 },
  panelLabel:        { fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' },

  // Action stack — 4 small buttons stacked vertically
  actionStack:       { flex: '1.4 1 0', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 },
  stackBtn:          { flex: 1, padding: '0 6px', background: '#141c30', color: '#9a8c73', border: '1px solid #4a3d28', borderRadius: 4, fontSize: 11, fontWeight: 700, fontFamily: "'Press Start 2P', monospace", cursor: 'pointer', minHeight: 28, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  stackBtnDisabled:  { opacity: 0.4, cursor: 'not-allowed' },
  stackBtnActive:    { borderColor: '#d4a847', color: '#d4a847', background: 'rgba(212,168,71,0.1)' },
  stackBtnDash:      { background: 'rgba(74,222,128,0.15)', borderColor: '#4ade80', color: '#86efac' },
  stackBtnProne:     { background: '#3a2a10', color: '#f5c842', borderColor: '#8b7340' },
  stackBtnEngaged:   { background: '#3a1a1a', color: '#f5a8a8', borderColor: '#c03333' },
  stackBtnInspired:  { background: 'rgba(251,191,36,0.18)', borderColor: '#fbbf24', color: '#fde68a', boxShadow: '0 0 10px rgba(251,191,36,0.4)' },

  endTurnFull:       { width: '100%', marginTop: 8, padding: '12px 0', background: 'linear-gradient(180deg, #d4a847, #a68330)', border: '1px solid #c4a44a', color: '#1a0e00', fontSize: 15, fontWeight: 800, fontFamily: "'Press Start 2P', monospace", cursor: 'pointer', borderRadius: 5, boxShadow: '0 4px 16px rgba(212,168,71,0.4)', letterSpacing: '0.04em' },
  levelBtn:          { background: '#142038', border: '1px solid #2a4a60', color: '#7ab8e0' },
  statusPills:       { display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6, justifyContent: 'center' },
  condPill:          { fontSize: 10, background: 'rgba(192,57,43,0.15)', border: '1px solid rgba(192,57,43,0.5)', color: '#e07070', borderRadius: 3, padding: '2px 7px', textTransform: 'capitalize' },
  buffPill:          { fontSize: 10, background: 'rgba(212,168,71,0.1)', border: '1px solid', borderRadius: 3, padding: '2px 7px', display: 'inline-flex', alignItems: 'center', gap: 4 },
  buffPillX:         { background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 9, opacity: 0.6, padding: 0, lineHeight: 1, color: 'inherit' },
  buffDurBadge:      { fontSize: 9, fontWeight: 700, background: 'rgba(0,0,0,0.35)', borderRadius: 8, padding: '0 6px', color: 'inherit', lineHeight: '14px', height: 14 },
  buffDurBadgeWarn:  { background: 'rgba(192,57,43,0.55)', color: '#ffd0c0', animation: 'pulse 1.4s ease-in-out infinite' },
  noToken:           { textAlign: 'center', color: '#6a5d45', fontSize: 13, padding: '12px 0' },
  deathSavePanel:    { background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.3)', borderRadius: 6, padding: '12px 16px', marginBottom: 8 },
  deathSaveTitle:    { fontSize: 14, fontWeight: 800, fontFamily: "'Press Start 2P', monospace", color: '#f87171', textAlign: 'center', marginBottom: 10 },
  deathSaveRow:      { display: 'flex', justifyContent: 'center', gap: 32, marginBottom: 10 },
  deathSaveGroup:    { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 },
  deathSaveLabel:    { fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#9a8b6e' },
  deathSavePips:     { display: 'flex', gap: 6 },
  deathSavePip:      { width: 16, height: 16, borderRadius: '50%', border: '2px solid #4a3d28', transition: 'background 0.2s' },
  deathSaveResult:   { fontSize: 16, fontWeight: 800, fontFamily: "'Press Start 2P', monospace", color: '#4ade80', textAlign: 'center', padding: '8px 0' },
  deathSaveInput:    { display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' },
  deathRollLabel:    { fontSize: 11, color: '#9a8b6e', fontWeight: 700 },
  deathRollInput:    { width: 60, background: '#0e1528', border: '1px solid #4a3d28', borderRadius: 4, color: '#f0e6d0', padding: '6px 8px', fontSize: 18, fontWeight: 800, outline: 'none', textAlign: 'center' },
  deathRollBtn:      { background: 'linear-gradient(180deg, #d4a847, #a68330)', border: '1px solid #c4a44a', borderRadius: 4, color: '#1a0e00', padding: '6px 16px', fontSize: 14, fontWeight: 800, fontFamily: "'Press Start 2P', monospace", cursor: 'pointer' },
  doorPrompt:        { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 },
  doorBtn:           { flex: 1, height: 38, background: 'rgba(139,115,64,0.15)', border: '1px solid #8b7340', borderRadius: 4, color: '#d4a847', fontSize: 14, fontWeight: 700, fontFamily: "'Press Start 2P', monospace", cursor: 'pointer' },
  doorMsg:           { fontSize: 12, color: '#d4943a' },
  pingHint:          { position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)', background: 'rgba(10,15,30,0.92)', border: '1px solid #4a3d28', borderRadius: 6, padding: '8px 14px', color: '#f0e6d0', fontSize: 13, fontWeight: 600, zIndex: 50, display: 'flex', alignItems: 'center', gap: 10, boxShadow: '0 4px 16px rgba(0,0,0,0.5)' },
  waitingChip:       { position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)', maxWidth: 'calc(100vw - 24px)', background: 'rgba(10,15,30,0.92)', backdropFilter: 'blur(8px)', border: '1px solid #d4a847', borderRadius: 6, padding: '6px 12px', color: '#f0e6d0', fontSize: 12, fontWeight: 600, fontFamily: "'Press Start 2P', monospace", zIndex: 8, display: 'flex', alignItems: 'center', gap: 8, boxShadow: '0 4px 16px rgba(212,168,71,0.25)', flexWrap: 'wrap', justifyContent: 'center' },
  waitingRerollBtn:  { background: 'transparent', border: '1px solid #4a3d28', borderRadius: 4, color: '#d4a847', padding: '2px 8px', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Press Start 2P', monospace", letterSpacing: '0.04em' },
  pingHintCancel:    { background: 'transparent', border: '1px solid #4a3d28', borderRadius: 4, color: '#9a8b6e', fontSize: 12, padding: '3px 10px', cursor: 'pointer' },
  waitingScreen:     { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, background: 'radial-gradient(ellipse at 50% 30%, #162844 0%, #0a0f1e 70%)' },
  waitingOverlay:    { position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, background: 'rgba(5,8,18,0.45)', backdropFilter: 'blur(2px)', zIndex: 20, pointerEvents: 'none' },
  waitingCard:       { width: '100%', maxWidth: 360, padding: '28px 24px 22px', textAlign: 'center', background: 'linear-gradient(180deg, #1a2a48, #142038)', border: '2px solid #8b7340', borderRadius: 6, boxShadow: '0 20px 60px rgba(0,0,0,0.7)', pointerEvents: 'auto' },
  rerollBtn:         { marginTop: 16, padding: '8px 14px', background: 'rgba(212,168,71,0.12)', border: '1px solid #4a3d28', borderRadius: 4, color: '#d4a847', fontSize: 12, fontWeight: 700, fontFamily: "'Press Start 2P', monospace", cursor: 'pointer', letterSpacing: '0.04em' },
  waitingContent:    { width: '100%', maxWidth: 340, textAlign: 'center' },
  waitingIcon:       { fontSize: 64, marginBottom: 20 },
  waitingTitle:      { fontSize: 26, fontWeight: 800, fontFamily: "'Press Start 2P', monospace", color: '#d4a847', textShadow: '0 2px 8px rgba(212,168,71,0.3)', marginBottom: 10 },
  waitingSub:        { fontSize: 15, color: '#f0e6d0', marginBottom: 8, fontWeight: 600 },
  waitingHint:       { fontSize: 13, color: '#6a5d45', lineHeight: 1.5, marginBottom: 24 },
  waitingPulse:      { width: 40, height: 4, borderRadius: 2, background: 'linear-gradient(90deg, #4a3d28, #d4a847, #4a3d28)', margin: '0 auto', animation: 'pulse 2s ease-in-out infinite' },
  battleScreen:      { width: '100%', height: '100%', background: '#080510', display: 'flex', alignItems: 'center', justifyContent: 'center', animation: 'none' },
  battleContent:     { textAlign: 'center' },
  battleSwords:      { fontSize: 80, marginBottom: 24, display: 'block', animation: 'pulse 0.5s ease-in-out infinite alternate' },
  battleTitle:       { fontSize: 42, fontWeight: 900, fontFamily: "'Press Start 2P', monospace", color: '#c0392b', letterSpacing: '0.12em', textShadow: '0 0 40px rgba(192,57,43,0.8), 0 0 80px rgba(192,57,43,0.4)', marginBottom: 12 },
  battleSub:         { fontSize: 18, fontFamily: "'Press Start 2P', monospace", color: '#9a8b6e', letterSpacing: '0.1em', textTransform: 'uppercase' },

  rollPhaseScreen:   { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, background: 'radial-gradient(ellipse at 50% 30%, #1a2040 0%, #0a0f1e 70%)' },
  rollPhaseContent:  { width: '100%', maxWidth: 340, textAlign: 'center' },
  rollPhaseIcon:     { fontSize: 56, marginBottom: 16 },
  rollPhaseTitle:    { fontSize: 26, fontWeight: 800, fontFamily: "'Press Start 2P', monospace", color: '#d4a847', marginBottom: 8, textShadow: '0 2px 8px rgba(212,168,71,0.3)' },
  rollPhaseSubtitle: { fontSize: 14, color: '#9a8b6e', marginBottom: 28, lineHeight: 1.5 },

  rollInputCard:     { background: 'linear-gradient(180deg, #1a2a48, #142038)', border: '2px solid #8b7340', borderRadius: 6, padding: '24px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, maxWidth: 360, margin: '0 auto' },
  rollInputLabel:    { fontSize: 12, fontWeight: 700, fontFamily: "'Press Start 2P', monospace", textTransform: 'uppercase', letterSpacing: '0.08em', color: '#9a8b6e' },
  rollFormulaChip:   { fontSize: 18, fontWeight: 800, fontFamily: "'Press Start 2P', monospace", color: '#d4a847', background: '#0e1528', border: '1px solid #4a3d28', borderRadius: 6, padding: '6px 16px' },
  rollAdvRow:        { display: 'flex', gap: 6, width: '100%' },
  rollAdvBtn:        { flex: 1, padding: '8px 0', background: '#0e1528', border: '1px solid #4a3d28', borderRadius: 4, color: '#9a8b6e', fontSize: 11, fontWeight: 700, fontFamily: "'Press Start 2P', monospace", cursor: 'pointer', letterSpacing: '0.04em' },
  rollDigitalBtn:    { width: '100%', padding: '14px 0', background: 'linear-gradient(180deg, #d4a847, #a68330)', border: '1px solid #c4a44a', borderRadius: 4, color: '#1a0e00', fontSize: 18, fontWeight: 800, fontFamily: "'Press Start 2P', monospace", cursor: 'pointer', boxShadow: '0 4px 14px rgba(212,168,71,0.35)' },
  rollDivider:       { fontSize: 11, color: '#6a5d45', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '4px 0', fontStyle: 'italic' },
  rollBigInput:      { width: 120, fontSize: 52, fontWeight: 800, textAlign: 'center', background: '#0e1528', border: '2px solid #8b7340', borderRadius: 4, color: '#f0e6d0', padding: '12px 0', outline: 'none', fontFamily: "'Press Start 2P', monospace" },
  rollSubmitBtn:     { width: '100%', padding: '12px 0', background: '#1a2a48', border: '1px solid #4a3d28', borderRadius: 4, color: '#9a8b6e', fontSize: 14, fontWeight: 700, fontFamily: "'Press Start 2P', monospace", cursor: 'pointer', marginTop: 4 },

  rollSubmittedCard: { background: 'linear-gradient(180deg, #1a2a48, #142038)', border: '2px solid rgba(74,154,90,0.5)', borderRadius: 6, padding: '28px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 },
  rollSubmittedIcon: { fontSize: 44, color: '#5cb870' },
  rollSubmittedText: { fontSize: 20, fontWeight: 800, fontFamily: "'Press Start 2P', monospace", color: '#5cb870' },
  rollSubmittedTotal:{ fontSize: 16, color: '#f0e6d0' },
  rollWaiting:       { fontSize: 13, color: '#6a5d45', marginTop: 8 },
  initiativePanel:   { background: 'linear-gradient(180deg, #1a2a48, #142038)', border: '2px solid #8b7340', borderRadius: 6, width: '100%', maxWidth: 360, maxHeight: '70vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.7)' },
  initiativeHeader:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', borderBottom: '2px solid #4a3d28', fontWeight: 700, fontFamily: "'Press Start 2P', monospace", fontSize: 14, color: '#d4a847' },
  closeBtn:          { background: 'transparent', border: 'none', color: '#9a8b6e', fontSize: 16, cursor: 'pointer', padding: '0 4px', borderRadius: 4 },
  initiativeList:    { listStyle: 'none', overflowY: 'auto', padding: '8px 0' },
  initEntry:         { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 16px', fontSize: 14 },
  initActive:        { background: 'rgba(212,168,71,0.12)', borderLeft: '3px solid #d4a847' },
  initDot:           { width: 10, height: 10, borderRadius: '50%', flexShrink: 0 },
  buffsOverlay:      { position: 'absolute', top: 44, left: 0, right: 0, bottom: 0, background: 'rgba(5,8,18,0.75)', zIndex: 20, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 16 },
  initiativeOverlay: { position: 'absolute', top: 44, left: 0, right: 0, bottom: 0, background: 'rgba(5,8,18,0.75)', zIndex: 20, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 16 },
  buffsPanel:        { background: 'linear-gradient(180deg, #1a2a48, #142038)', border: '2px solid #8b7340', borderRadius: 6, width: '100%', maxWidth: 360, maxHeight: '80vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.7)' },
  buffsPanelHeader:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', borderBottom: '2px solid #4a3d28', fontWeight: 700, fontFamily: "'Press Start 2P', monospace", fontSize: 14, color: '#d4a847' },
  buffsPanelBody:    { overflowY: 'auto', padding: '12px 16px', flex: 1 },
};
